<template>
  <div class="app-shell">
    <!-- 顶部导航栏 -->
    <header class="top-nav">
      <div class="logo-area">
        <div class="logo-icon" @click="goHome" style="cursor:pointer">
          <img :src="publicPath + 'favicon.ico'" alt="logo" />
        </div>
        <div class="logo-text" @click="goHome" style="cursor:pointer">
          <span class="system-name">生物医学新技术在线服务系统</span>
        </div>
      </div>

      <div class="user-area">
        <el-icon class="top-icon" :size="18"><Bell /></el-icon>
        <div class="user-info">
          <div class="user-avatar">{{ displayRoleName.charAt(0) }}</div>
          <el-dropdown v-if="hasMultipleRoles" trigger="click" @command="onSwitchRole" placement="bottom-end">
            <span class="role-pill">{{ displayRoleName }}</span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item v-for="r in roleOptions" :key="r.id" :command="r.id" :class="{ 'is-active': currentRoleId === r.id }">{{ r.name }}</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <span v-else>{{ displayRoleName }}</span>
          <span class="divider" />
          <span>{{ displayUsername }}</span>
        </div>
        <el-icon class="top-action-btn" :size="18" title="全屏切换" @click="toggleFullscreen"><FullScreen /></el-icon>
        <el-icon class="top-action-btn top-action-logout" :size="18" title="退出登录" @click="onLogout"><SwitchButton /></el-icon>
      </div>
    </header>

    <!-- 标签页栏（融入顶部深蓝） -->
    <nav class="tabs-bar">
      <div class="tabs-list">
        <button
          type="button"
          class="top-tab"
          :class="{ active: isHomePage }"
          @click="goHome"
        >
          <el-icon :size="14"><HomeFilled /></el-icon>
          <span>首页</span>
        </button>

        <button
          v-for="(tab, index) in openedTabs"
          :key="tab.path"
          type="button"
          class="top-tab"
          :class="{ active: route.path === tab.path }"
          @click="goTo(tab.path)"
        >
          <span>{{ tab.name }}</span>
          <el-icon class="tab-close" :size="12" @click.stop="closeTab(index)">
            <Close />
          </el-icon>
        </button>
      </div>
    </nav>

    <div class="body-layout">
      <!-- 左侧菜单（首页不显示） -->
      <aside v-if="!isHomePage" class="sidebar">
        <nav class="menu-list">
          <template v-for="group in menuGroups" :key="group.key">
            <div class="menu-group-title">
              <el-icon :size="14">
                <component :is="group.icon" />
              </el-icon>
              <span>{{ group.title }}</span>
            </div>
            <template v-for="item in group.items" :key="menuKey(item)">
              <button
                type="button"
                class="menu-item"
                :class="{ active: isMenuActive(item), expanded: isMenuExpanded(item) }"
                @click="handleSelect(item)"
              >
                <el-icon :size="16">
                  <component :is="getIcon(item.meta.icon)" />
                </el-icon>
                <span>{{ item.name }}</span>
              </button>
              <div v-if="item.children && isMenuExpanded(item)" class="submenu-list">
                <button
                  v-for="child in item.children"
                  :key="menuKey(child)"
                  type="button"
                  class="submenu-item"
                  :class="{ active: isMenuActive(child) }"
                  @click="handleSelect(child)"
                >
                  {{ child.name }}
                </button>
              </div>
            </template>
          </template>
        </nav>
      </aside>

      <main :class="['main-content', { 'main-content-full': isHomePage }]">
        <router-view></router-view>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Bell, Close, HomeFilled, Menu, Refresh, Setting, Warning, InfoFilled, FullScreen, SwitchButton } from "@element-plus/icons-vue";
import store from "./store";
import { ElMessageBox } from 'element-plus';
import { logoutApi } from "@/api/base";

const route = useRoute();
const router = useRouter();
const publicPath = process.env.BASE_URL;
const userInfo = computed(() => store.state.userInfo);
const isHomePage = computed(() => route.path === '/home');

// 专家角色：仅显示专家信息管理
const isExpertRole = computed(() => {
  try {
    const sr = JSON.parse(localStorage.getItem('selectedRole') || 'null')
    return sr && (sr.id || sr) === 'bio_center_expert'
  } catch (e) {
    return false
  }
})

// 当前登录账号信息（从 localStorage 读取）
const displayUsername = computed(() => localStorage.getItem('name') || userInfo.value?.name || '管理员')

// 全屏切换
const toggleFullscreen = () => {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch(() => {})
  } else {
    document.exitFullscreen().catch(() => {})
  }
}

// 退出登录
const onLogout = async () => {
  try { await logoutApi() } catch (e) {}
  localStorage.removeItem('Token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('roles')
  localStorage.removeItem('userId')
  localStorage.removeItem('name')
  localStorage.removeItem('selectedRole')
  localStorage.removeItem('userInfo')
  window.location.href = window.getSsoLoginURL()
}

// 切换角色（多角色用户）
const hasMultipleRoles = computed(() => {
  try {
    const roles = JSON.parse(localStorage.getItem('roles') || '[]')
    return roles.length > 1
  } catch (e) {
    return false
  }
})
const roleOptions = ref([])
try {
  const roles = JSON.parse(localStorage.getItem('roles') || '[]')
  roleOptions.value = roles.map(r => ({ id: r?.id || r, name: r?.name || r }))
} catch (e) {}
const currentRoleId = computed(() => {
  try {
    const sr = JSON.parse(localStorage.getItem('selectedRole') || 'null')
    return sr ? (sr.id || sr) : ''
  } catch (e) {
    return ''
  }
})
const EXPERT_ROUTES = ['/dashboard', '/expert', '/expertAudit', '/reviewRecord', '/evaluation', '/expertTag']
const ALLOWED_EXPERT_ROLES = ['bio_center_expert', 'country_admin', 'province_admin', 'city_admin', 'county_admin', 'bio_center', 'bio_center_leader', 'bio_center_member']
async function onSwitchRole(id) {
  const opt = roleOptions.value.find(o => o.id === id)
  if (!opt) return
  const inExpert = EXPERT_ROUTES.includes(route.path)
  const roleAllowed = ALLOWED_EXPERT_ROLES.includes(opt.id)
  if (inExpert && !roleAllowed) {
    try {
      await ElMessageBox.confirm(
        `切换为「${opt.name}」后将无法访问当前子系统,并将跳转至首页,是否继续?`,
        '切换确认',
        { confirmButtonText: '继续切换', cancelButtonText: '取消', type: 'warning' }
      )
    } catch (e) {
      return // 用户取消
    }
    localStorage.setItem('selectedRole', JSON.stringify(opt))
    window.location.href = '/#/home'
    return
  }
  localStorage.setItem('selectedRole', JSON.stringify(opt))
  window.location.reload()
}
const displayRoleName = computed(() => {
  try {
    const sr = JSON.parse(localStorage.getItem('selectedRole') || 'null')
    return sr?.name || '暂无角色'
  } catch (e) {
    return '暂无角色'
  }
})

// 业务导航菜单
const businessMenus = computed(() => {
  const all = [
    { path: '/dashboard', name: '工作台', meta: { title: '工作台', icon: 'ele-Odometer' } },
    { path: '/expert', name: '专家信息管理', meta: { title: '专家信息管理', icon: 'ele-User' } },
    { path: '/evaluation', name: '专家评审评价', meta: { title: '专家评审评价', icon: 'ele-EditPen' } },
    { path: '/reviewRecord', name: '绩效记录查询', meta: { title: '绩效记录查询', icon: 'ele-Document' } },
    { path: '/expertAudit', name: '专家级别审核', meta: { title: '专家级别审核', icon: 'ele-Checked' } },
  ]
  if (isExpertRole.value) return all.filter(m => m.path === '/expert')
  // 暂时隐藏：工作台、专家评审评价、绩效记录查询、专家级别审核
  const hiddenPaths = ['/dashboard', '/evaluation', '/reviewRecord', '/expertAudit']
  return all.filter(m => !hiddenPaths.includes(m.path))
});

// 系统设置菜单（专家角色不显示标签维护）
const systemMenus = computed(() => {
  if (isExpertRole.value) return []
  return [
    { path: '/expertTag', name: '专家标签维护', meta: { title: '专家标签维护', icon: 'ele-PriceTag' } },
  ]
});

// 监督检查菜单
const inspectMenus = computed(() => [
  { path: '/inspect', name: '检查总览', meta: { title: '检查总览', icon: 'ele-Grid' } },
  { path: '/inspect/criteria', name: '检查表管理', meta: { title: '检查表管理', icon: 'ele-Document' } },
  { path: '/inspect/annual-plan', name: '年度全国检查', meta: { title: '年度全国检查', icon: 'ele-Guide' } },
  { path: '/inspect/plans', name: '检查方案', meta: { title: '检查方案', icon: 'ele-Tickets' } },
  {
    path: '/inspect/activities',
    name: '检查活动',
    meta: { title: '检查活动', icon: 'ele-User' },
    children: [
      { path: '/inspect/activities', name: '专家检查', query: { tab: 'check' }, meta: { title: '专家检查' } },
      { path: '/inspect/activities', name: '机构整改', query: { tab: 'rectify' }, meta: { title: '机构整改' } },
      { path: '/inspect/activities', name: '专家复查', query: { tab: 'review' }, meta: { title: '专家复查' } },
    ],
  },
  { path: '/inspect/self-check', name: '机构自查', meta: { title: '机构自查', icon: 'ele-UserFilled' } },
  { path: '/inspect/history', name: '历史记录', meta: { title: '历史记录', icon: 'ele-Clock' } },
  { path: '/inspect/rectification', name: '问题整改', meta: { title: '问题整改', icon: 'ele-Tools' } },
  { path: '/inspect/statistics', name: '数据统计', meta: { title: '数据统计', icon: 'ele-DataAnalysis' } },
  { path: '/inspect/settings', name: '系统设置', meta: { title: '系统设置', icon: 'ele-Setting' } },
]);

// 当前子系统：根据路由路径判断（inspect / expert）
const currentSubsystem = computed(() => {
  if (route.path.startsWith('/inspect')) return 'inspect';
  return 'expert';
});

// 菜单分组（根据当前子系统过滤，只显示对应菜单）
const menuGroups = computed(() => {
  if (currentSubsystem.value === 'inspect') {
    return [{ key: 'inspect', title: '监督检查', icon: Warning, items: inspectMenus.value }];
  }
  const groups = [
    { key: 'business', title: '业务导航', icon: Menu, items: businessMenus.value },
  ]
  if (systemMenus.value.length) {
    groups.push({ key: 'system', title: '系统设置', icon: Setting, items: systemMenus.value })
  }
  return groups
});

// 当前子系统的所有菜单项（供标签页查找用）
const allMenus = computed(() => {
  if (currentSubsystem.value === 'inspect') {
    return inspectMenus.value;
  }
  return [...businessMenus.value, ...systemMenus.value];
});

// 已打开的标签页（不含首页）
const MAX_OPENED_TABS = 8;
const openedTabs = ref([]);

// 根据路由初始化 / 更新标签页
watch(
  () => route.path,
  (newPath) => {
    if (newPath === '/home') {
      return;
    }
    const exists = openedTabs.value.some((t) => t.path === newPath);
    if (!exists) {
      const menuItem = allMenus.value.find((m) => m.path === newPath);
      if (menuItem) {
        openedTabs.value.push({ path: menuItem.path, name: menuItem.name });
        trimOpenedTabs();
      }
    }
  },
  { immediate: true }
);

function trimOpenedTabs() {
  while (openedTabs.value.length > MAX_OPENED_TABS) {
    openedTabs.value.shift();
  }
}

function closeTab(index) {
  const tab = openedTabs.value[index];
  if (!tab) return;
  const isActive = route.path === tab.path;
  openedTabs.value.splice(index, 1);
  if (isActive) {
    if (openedTabs.value.length > 0) {
      const nextTab = openedTabs.value[Math.max(0, index - 1)];
      router.push(nextTab.path);
    } else {
      router.push('/home');
    }
  }
}

function menuKey(item) {
  return `${item.path}-${item.query?.tab || item.name}`;
}

function activityTab() {
  return route.path === '/inspect/activities' ? route.query.tab || 'check' : '';
}

function isMenuActive(item) {
  if (item.children?.length) {
    return item.path === route.path;
  }
  if (item.path !== route.path) return false;
  if (item.query?.tab) return activityTab() === item.query.tab;
  return true;
}

function isMenuExpanded(item) {
  return Boolean(item.children?.length && item.path === route.path);
}

function handleSelect(item) {
  if (item.children?.length) {
    router.push({ path: item.path, query: item.children[0].query });
    return;
  }
  router.push({ path: item.path, query: item.query || {} });
}

function goHome() {
  router.push('/home');
}

function getIcon(e) {
  return e ? e.substr(4) : 'Menu';
}

function goTo(path) {
  router.push(path);
}
</script>

<style lang="scss" scoped>
// 与 new-technology-inspect 项目保持一致的主题色板
$c-deep-blue: #1048a3;
$c-primary: #1890ff;
$c-primary-light: #e6f7ff;
$c-page-bg: #f0f2f5;
$c-border-light: #ebeef5;
$c-border: #dcdfe6;
$c-text-primary: #303133;
$c-text-regular: #606266;
$c-text-secondary: #909399;

.role-pill { display:inline-flex; align-items:center; padding:4px 14px; background:#2563eb; color:#fff; border-radius:14px; cursor:pointer; font-size:13px; line-height:1.5; transition:background .2s; }
.role-pill:hover { background:#1d4ed8; }
:deep(.el-dropdown-menu__item.is-active) { color:#1890ff; background:#e6f7ff; }
.role-tip { display:flex; align-items:center; gap:8px; color:#666; margin-bottom:16px; font-size:14px; }
.role-list { display:flex; flex-direction:column; gap:8px; }
.role-item { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border:1px solid #e8e8e8; border-radius:6px; cursor:pointer; transition:all .2s; }
.role-item:hover { border-color:#1890ff; }
.role-item.active { background:#e6f7ff; border-color:#1890ff; }
.role-name { font-size:15px; color:#333; }
.role-status.is-selected { color:#1890ff; font-size:13px; }
.role-status.is-unselected { color:#999; font-size:13px; }

.app-shell {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  flex-direction: column;
  background: $c-page-bg;
}

// 顶部导航栏
.top-nav {
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 24px;
  background: $c-deep-blue;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: #fff;
  flex-shrink: 0;
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 26px;
    height: 26px;
    object-fit: contain;
  }
}

.logo-text {
  display: flex;
  min-width: 0;
  flex-direction: column;
  line-height: 1.2;
}

.system-name {
  overflow: hidden;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.system-sub {
  overflow: hidden;
  margin-top: 2px;
  font-size: 11px;
  opacity: 0.72;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-area {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.top-icon {
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  &:hover { color: #fff; }
}

.top-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: color 0.2s;

  &:hover { color: #fff; }
}

.top-action-logout {
  &:hover { color: #f56c6c; }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
}

.user-info .el-dropdown {
  color: inherit;
  font-size: inherit;
  font-family: inherit;
}

.user-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $c-primary;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.divider {
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.25);
}

// 标签页栏（融入顶部深蓝）
.tabs-bar {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 16px;
  background: $c-deep-blue;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.tabs-list {
  display: flex;
  height: 100%;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
}

.top-tab {
  display: inline-flex;
  align-items: center;
  height: 30px;
  gap: 6px;
  border: 0;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  font-size: 13px;
  padding: 0 10px;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
  }

  &.active {
    background: $c-page-bg;
    color: $c-text-primary;
    font-weight: 700;
  }
}

.tab-close {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 12px;

  &:hover {
    background: rgba(0, 0, 0, 0.18);
    color: #fff;
  }
}

// 主体横向布局：侧边栏 + 主区域
.body-layout {
  display: flex;
  min-height: 0;
  flex: 1;
}

// 左侧菜单
.sidebar {
  width: 220px;
  flex-shrink: 0;
  border-right: 1px solid $c-border-light;
  background: #fff;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 0;
}

.sidebar-title {
  color: $c-text-secondary;
  font-size: 12px;
  letter-spacing: 1px;
  padding: 8px 24px 12px;
}

.menu-list {
  display: flex;
  flex-direction: column;
}

.menu-group-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 6px;
  color: $c-text-secondary;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;

  .el-icon {
    color: $c-text-secondary;
  }
}

.menu-item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 12px;
  border: 0;
  border-left: 3px solid transparent;
  background: #fff;
  color: $c-text-regular;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.4;
  padding: 10px 20px 10px 21px;
  text-align: left;
  transition: background 0.2s, color 0.2s, border-color 0.2s;

  &:hover {
    background: $c-page-bg;
    color: $c-primary;
  }

  &.active {
    border-left-color: $c-primary;
    background: $c-primary-light;
    color: $c-primary;
    font-weight: 700;
  }

  &.expanded {
    border-left-color: $c-primary;
    background: $c-primary-light;
    color: $c-primary;
  }

  .el-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
}

.submenu-list {
  display: flex;
  flex-direction: column;
  padding: 6px 0 8px;
}

.submenu-item {
  width: 100%;
  border: 0;
  background: transparent;
  color: $c-text-regular;
  cursor: pointer;
  font-size: 15px;
  line-height: 1.4;
  padding: 10px 24px 10px 54px;
  text-align: left;
  transition: color 0.2s, background 0.2s;

  &:hover {
    background: $c-page-bg;
    color: $c-primary;
  }

  &.active {
    color: $c-primary;
    font-weight: 700;
  }
}

// 主内容区
.main-content {
  min-width: 0;
  flex: 1;
  overflow: auto;
  background: $c-page-bg;
  padding: 16px;

  &.main-content-full {
    padding: 20px 28px;
  }
}

// 自定义滚动条
:deep(::-webkit-scrollbar) {
  width: 6px;
  height: 6px;
}
:deep(::-webkit-scrollbar-thumb) {
  background: #c0c4cc;
  border-radius: 3px;
}
:deep(::-webkit-scrollbar-thumb:hover) {
  background: #909399;
}
</style>
